/**
 * Review queue screen — WO-M2.
 *
 * Lists all pending FileResults from the current session's BatchResults
 * (held in the Zustand queue store). For each pending file:
 *   - ReviewCard with filename, confidence, ReconciliationFlag, FactList
 *   - Confirm → POST /api/sb/batches/:batchId/files/:fileId/confirm
 *   - Discard → POST /api/sb/batches/:batchId/files/:fileId/discard
 *
 * After confirm, the CommitResult is available and the user can open
 * CorrectionModal on any committed FactRecord.
 *
 * CRITICAL ORDERING of correctFact: the factId required by
 * POST /api/sb/facts/:factId/correct is the id from CommitResult.committedRecords.
 * This is only available after a successful confirmFile call. The correction
 * UI is therefore surfaced post-confirmation, not during pending review.
 *
 * The pending store is in-memory for this session. Durable offline
 * persistence is WO-M3 (out of scope here).
 *
 * submitterId: the current session user's id (from useSession().user.id),
 * satisfying the ConfirmRequest.submitterId field without PII in URLs.
 *
 * NEVER imports @smartbox/ui (mobile CLAUDE.md invariant 1).
 *
 * WO-M0 — design-system refactor. All styling via theme tokens + UI primitives.
 */

import { useMemo, useState } from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useQueueStore, selectPendingFiles } from '../../src/review/queueStore';
import { confirmFile, discardFile } from '../../src/api/smartbox';
import { useSession } from '../../src/auth/session';
import { ReviewCard } from '../../src/components/review/ReviewCard';
import { CorrectionModal } from '../../src/components/review/CorrectionModal';
import { ApiRequestError } from '../../src/api/client';
import type { FactRecord, CommitResult } from '@beam/api-types';
import { useConnectivity } from '../../src/offline/useConnectivity';
import { SyncStatusBar } from '../../src/offline/SyncStatusBar';
import { Screen, Text, Button, Card, Field, ListRow, Badge } from '../../src/components/ui';
import { theme } from '../../src/theme';
import { useHelpEntry } from '../../src/help';
import { SCREEN_KEYS } from '../../src/help';
// WO-25: provenance navigation (factId is an opaque UUID — no PII in URL per CLAUDE.md §2)

/** Per-file transient action state (loading + error). */
interface FileActionState {
  isActing: boolean;
  error: string | null;
}

export default function ReviewScreen() {
  const emptyHelp = useHelpEntry(SCREEN_KEYS.review, 'empty');
  const router = useRouter();
  const sessionUser = useSession((s) => s.user);
  const connectivity = useConnectivity();

  // Queue store
  const pendingFiles = useQueueStore(selectPendingFiles);
  const commits = useQueueStore((s) => s.commits);
  const markConfirmed = useQueueStore((s) => s.markConfirmed);
  const markDiscarded = useQueueStore((s) => s.markDiscarded);

  // Client-side filter of the already-loaded pending files (by filename).
  const [query, setQuery] = useState('');
  const visibleFiles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingFiles;
    return pendingFiles.filter(({ fileResult }) =>
      (fileResult.filename ?? '').toLowerCase().includes(q),
    );
  }, [pendingFiles, query]);

  // Per-file action state (fileId → state)
  const [actionStates, setActionStates] = useState<Record<string, FileActionState>>({});

  // Committed facts shown after confirmation — tracks which fileId's commit is expanded
  const [expandedCommits, setExpandedCommits] = useState<Set<string>>(new Set());

  // CorrectionModal state
  const [correctionFact, setCorrectionFact] = useState<FactRecord | null>(null);
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function setActing(fileId: string, acting: boolean) {
    setActionStates((prev) => ({
      ...prev,
      [fileId]: { ...prev[fileId], isActing: acting, error: acting ? (prev[fileId]?.error ?? null) : prev[fileId]?.error ?? null },
    }));
  }

  function setActionError(fileId: string, error: string | null) {
    setActionStates((prev) => ({
      ...prev,
      [fileId]: { isActing: false, error },
    }));
  }

  function getActionState(fileId: string): FileActionState {
    return actionStates[fileId] ?? { isActing: false, error: null };
  }

  function extractError(err: unknown): string {
    if (err instanceof ApiRequestError) return err.message;
    if (err instanceof Error) return err.message;
    return 'An unexpected error occurred. Please try again.';
  }

  // ---------------------------------------------------------------------------
  // Confirm handler
  // ---------------------------------------------------------------------------

  async function handleConfirm(batchId: string, fileId: string) {
    const userId = sessionUser?.id;
    if (!userId) {
      setActionError(fileId, 'Not signed in — cannot confirm.');
      return;
    }

    setActing(fileId, true);
    setActionError(fileId, null);
    try {
      const commitResult: CommitResult = await confirmFile(batchId, fileId, userId);
      markConfirmed(batchId, fileId, commitResult);
      // Expand committed facts panel so user can optionally correct.
      setExpandedCommits((prev) => new Set(prev).add(fileId));
    } catch (err) {
      setActionError(fileId, extractError(err));
    } finally {
      setActing(fileId, false);
    }
  }

  // ---------------------------------------------------------------------------
  // Discard handler
  // ---------------------------------------------------------------------------

  async function handleDiscard(batchId: string, fileId: string) {
    setActing(fileId, true);
    setActionError(fileId, null);
    try {
      await discardFile(batchId, fileId);
      markDiscarded(batchId, fileId);
    } catch (err) {
      setActionError(fileId, extractError(err));
    } finally {
      setActing(fileId, false);
    }
  }

  // ---------------------------------------------------------------------------
  // Correction handlers
  // ---------------------------------------------------------------------------

  function openCorrection(fact: FactRecord) {
    setCorrectionFact(fact);
    setCorrectionModalVisible(true);
  }

  function handleCorrected(_factId: string) {
    // The store already holds the CommitResult; the backend has updated the fact.
    // A future enhancement could re-fetch the fact to show the updated value.
    setCorrectionModalVisible(false);
    setCorrectionFact(null);
  }

  // ---------------------------------------------------------------------------
  // Committed facts panel (shown after confirm)
  // ---------------------------------------------------------------------------

  function CommittedFactsPanel({ fileId }: { fileId: string }) {
    const commit = commits[fileId];
    if (!commit) return null;
    const isExpanded = expandedCommits.has(fileId);

    return (
      <Card style={styles.committedPanel}>
        <ListRow
          title={`Committed — ${commit.committedRecords.length} ${commit.committedRecords.length === 1 ? 'fact' : 'facts'}`}
          right={<Badge status="paid" label={isExpanded ? '▲' : '▼'} />}
          onPress={() =>
            setExpandedCommits((prev) => {
              const next = new Set(prev);
              if (next.has(fileId)) next.delete(fileId);
              else next.add(fileId);
              return next;
            })
          }
        />

        {isExpanded &&
          commit.committedRecords.map((fact) => (
            <View key={fact.id} style={styles.committedFactRow}>
              <View style={styles.committedFactInfo}>
                <Text variant="label" color="secondary">{fact.category}</Text>
                <Text variant="body" numberOfLines={2}>
                  {typeof fact.value === 'string'
                    ? fact.value
                    : JSON.stringify(fact.value)}
                </Text>
              </View>
              <View style={styles.factActions}>
                <Button
                  title="Correct"
                  variant="secondary"
                  size="sm"
                  fullWidth={false}
                  onPress={() => openCorrection(fact)}
                  accessibilityLabel={`Correct fact: ${fact.category}`}
                />
                {/* WO-25: provenance affordance — factId is opaque UUID, no PII in URL */}
                <Button
                  title="Chain"
                  variant="ghost"
                  size="sm"
                  fullWidth={false}
                  onPress={() => router.push(`/(app)/provenance/${fact.id}`)}
                  accessibilityLabel={`View provenance for ${fact.category}`}
                />
              </View>
            </View>
          ))}
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Screen padded={false} edges={['top', 'bottom']}>
      {/* WO-M3: offline sync status indicator */}
      <SyncStatusBar {...connectivity} />

      {pendingFiles.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text variant="title" color="secondary" accessibilityElementsHidden>✓</Text>
          <Text variant="title">Queue is empty</Text>
          <Text variant="body" color="secondary" style={styles.emptySubtitle}>
            {emptyHelp?.body ?? 'All pending files have been confirmed or discarded.'}
          </Text>
          <Button
            title="Capture a Receipt"
            variant="primary"
            size="md"
            fullWidth={false}
            onPress={() => router.push('/(app)/capture')}
            accessibilityLabel="Capture a new receipt"
          />
        </View>
      ) : (
        <Screen scroll padded style={styles.scrollInner}>
          <Field
            placeholder="Search by filename"
            value={query}
            onChangeText={setQuery}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
            clearButtonMode="while-editing"
            accessibilityLabel="Search pending files"
          />
          <Text variant="label" color="secondary">
            {visibleFiles.length}{' '}
            {visibleFiles.length === 1 ? 'file' : 'files'} pending review
          </Text>

          {visibleFiles.length === 0 ? (
            <Text variant="body" color="secondary">
              No files match “{query.trim()}”.
            </Text>
          ) : null}

          {visibleFiles.map(({ batchId, fileResult }) => {
            const { fileId } = fileResult;
            const actionState = getActionState(fileId);
            const commit = commits[fileId];

            return (
              <View key={fileId} style={styles.fileSection}>
                <ReviewCard
                  fileResult={fileResult}
                  onConfirm={() => handleConfirm(batchId, fileId)}
                  onDiscard={() => handleDiscard(batchId, fileId)}
                  isActing={actionState.isActing}
                  actionError={actionState.error}
                />
                {commit ? <CommittedFactsPanel fileId={fileId} /> : null}
              </View>
            );
          })}

          {/* Also show committed files that were just confirmed (session) */}
        </Screen>
      )}

      {/* Correction modal — opened per committed fact */}
      <CorrectionModal
        visible={correctionModalVisible}
        fact={correctionFact}
        onClose={() => {
          setCorrectionModalVisible(false);
          setCorrectionFact(null);
        }}
        onCorrected={handleCorrected}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  scrollInner: {
    flex: 1,
  },
  fileSection: {
    gap: theme.spacing.sm,
  },
  // Empty state
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
    gap: theme.spacing.md,
  },
  emptySubtitle: {
    textAlign: 'center',
  },
  // Committed facts panel
  committedPanel: {
    gap: theme.spacing.sm,
  },
  committedFactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing.sm,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    gap: theme.spacing.md,
  },
  committedFactInfo: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  factActions: {
    flexDirection: 'row',
    gap: theme.spacing.xs,
    alignItems: 'center',
  },
});
